import React, { FC, useEffect, useMemo, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { container } from 'src/inversify.config';
import { AutoComplete } from 'antd';
import debounce from 'lodash/debounce';
import { Query } from 'src/core/stores/data-store';
import { UserSummaryStore, UserSummary } from 'src/stores/users-store';
import { ImmutableObject } from '@hookstate/core';

const { Option } = AutoComplete;

const UserAutocomplete: FC<
  {
    onChange?: (value: { value: string; label: string }) => void;
    exclude?: string[];
    value?: { value: string; label: string };
    placeholder?: string;
    disabled?: boolean;
  } & WithTranslation
> = ({ t, onChange, exclude, value, placeholder, disabled }) => {
  const userStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = userStore.state;
  const [currentValue, setValue] = useState(value);

  const load = (search: string | undefined = undefined) => {
    const filter = (exclude || []).length === 0 ? [] : [`id ne ${exclude?.join(' and id ne ')}`];

    userStore.load({
      searchQuery: search,
      orderBy: [{ field: 'DisplayName', direction: 'Ascending', useProfile: true }],
      skip: 0,
      take: 1000000,
      filter,
    } as Query);
  };

  useEffect(() => {
    let nextValue = value || currentValue;
    if (nextValue && (exclude || []).includes(nextValue.value)) nextValue = undefined;
    if (nextValue !== currentValue) {
      setValue(nextValue);
      load(nextValue?.label);
    }
  }, [exclude, userStore, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = debounce((value: string) => {
    load(value || currentValue?.label);
  }, 800);

  const onValueChange = (value: string, option: any) => {
    let nextValue: { value: string; label: string } = null as any;
    if (option != null && option.value != null && option.name != null) {
      nextValue = { value, label: option.name };
    } else {
      const user = (usersState?.items?.value || []).find((x) => x.email === value || x.displayName === value);
      if (user) {
        nextValue = { value: user.id, label: user.displayName };
      } else {
        nextValue = { value, label: value };
      }
    }
    setValue(nextValue);
    onChange && onChange(nextValue);
  };

  return (
    <AutoComplete
      disabled={disabled}
      value={currentValue?.label}
      onChange={onValueChange}
      style={{ width: '100%', height: '100%' }}
      onSearch={handleSearch}
      placeholder={placeholder || t('SelectOrInviteUser')}
      onDropdownVisibleChange={(visible) => {
        if (visible) {
          load(currentValue?.label);
        }
      }}
    >
      {(usersState?.items?.value || []).map((o: ImmutableObject<UserSummary>) => (
        <Option key={o.id} value={o.id} name={o.displayName}>
          {o.displayName}
        </Option>
      ))}
    </AutoComplete>
  );
};

export default withTranslation()(UserAutocomplete);
